import { computed, makeObservable, observable, runInAction, action } from "mobx";
import { ApiSchema } from "@/modules/api";
import { WithAppStore } from "@/store/types";
import { AppStore } from "@/store";
import { StepGroups } from "@/components/onboarding/components/welcome-wizard/welcome-wizard.config";
import type { StepGroup } from "@/components/onboarding/components/welcome-wizard/welcome-wizard.types";
import { clientEnvModule } from "@/modules/client-env";
import { PerDeviceOnboardingInfoObservable } from "@/store/platform-info/PerDeviceOnboardingInfoObservable";

export type WelcomeStartStep =
  | "native-app-download"
  | "user-profile"
  | "app-selection"
  | "system-permissions-meeting-intro"
  | "system-permissions-voice-permissions";

export class PlatformOnboardingInfoObservable {
  store: AppStore;
  perDeviceOnboardingInfo: PerDeviceOnboardingInfoObservable;
  shouldShowIcpRfi: boolean;
  shouldShowImportCta: boolean;
  shouldShowHeadsUpCoachMark: boolean;
  welcomeStartStep: WelcomeStartStep;
  rfiCompletedSteps: string[];

  constructor({
    perDeviceOnboardingInfo,
    shouldShowIcpRfi,
    shouldShowImportCta,
    shouldShowHeadsUpCoachMark,
    rfiStartStep = "user-profile",
    rfiCompletedSteps = [],
    store,
  }: {
    perDeviceOnboardingInfo: PerDeviceOnboardingInfoObservable;
    shouldShowIcpRfi?: boolean;
    shouldShowImportCta?: boolean;
    shouldShowHeadsUpCoachMark?: boolean;
    rfiStartStep?: WelcomeStartStep;
    rfiCompletedSteps?: string[];
  } & WithAppStore) {
    this.store = store;
    this.perDeviceOnboardingInfo = perDeviceOnboardingInfo;
    this.shouldShowIcpRfi = shouldShowIcpRfi ?? false;
    this.shouldShowImportCta = shouldShowImportCta ?? false;
    this.shouldShowHeadsUpCoachMark = shouldShowHeadsUpCoachMark ?? false;
    this.welcomeStartStep = rfiStartStep;
    this.rfiCompletedSteps = rfiCompletedSteps;

    makeObservable(this, {
      store: false,
      perDeviceOnboardingInfo: false,
      shouldShowIcpRfi: observable,
      shouldShowImportCta: observable,
      shouldShowHeadsUpCoachMark: observable,
      welcomeStartStep: observable,
      rfiCompletedSteps: observable,
      shouldShowWelcomeOverlay: computed,
      welcomeStepGroups: computed,
      welcomeInitialStep: computed,
      markStepCompleted: action,
      setWelcomeStartStep: action,
    });
  }

  /**
   * This determines if the user gets the "Welcome" overlay, which walks them
   * through the onboarding process.
   *
   * Note that `perDeviceOnboardingInfo` is a device-specific, meaning that:
   * - Someone can go through the full "welcome" onboarding process on device 1...
   * - ...then logs on on device 2...
   * - ...they might be sent through part of the "welcome" process again (to set up things like
   *   device system permissions, etc.)
   */
  get shouldShowWelcomeOverlay() {
    return (
      this.shouldShowIcpRfi ||
      // If experiment is enabled, bypass all but shouldShowIcpRfi
      this.shouldShowImportCta ||
      this.perDeviceOnboardingInfo.shouldShowMeetingOnboardingSystemPermissions ||
      this.perDeviceOnboardingInfo.shouldShowVoiceOnboardingSystemPermissions
    );
  }

  get welcomeStepGroups(): StepGroup[] {
    const stepGroups: StepGroup[] = [];

    if (this.shouldShowIcpRfi) {
      stepGroups.push(StepGroups.ICP_RFI);
    }

    if (this.shouldShowImportCta) {
      stepGroups.push(StepGroups.IMPORT);
    }

    if (this.perDeviceOnboardingInfo.shouldShowMeetingOnboardingSystemPermissions) {
      stepGroups.push(StepGroups.SYSTEM_PERMISSIONS);
    }

    if (this.perDeviceOnboardingInfo.shouldShowVoiceOnboardingSystemPermissions) {
      stepGroups.push(StepGroups.SYSTEM_PERMISSIONS_VOICE);
    }

    return stepGroups;
  }

  get welcomeInitialStep(): WelcomeStartStep {
    // Check if user is on Mac, Windows, or iOS browser
    // These are the platforms we have native apps for.
    const shouldShowNativeAppDownload =
      clientEnvModule.isMacBrowser() ||
      clientEnvModule.isWindowsBrowser() ||
      clientEnvModule.isIosBrowser();

    // If user is on Mac/Windows/iOS browser, start with native-app-download
    if (shouldShowNativeAppDownload) {
      return "native-app-download";
    }

    // If we don't want to show the ICP RFI...
    if (!this.shouldShowIcpRfi) {
      // ...we prioritize showing the import CTA next.
      if (this.shouldShowImportCta) {
        return "app-selection";
      }

      // /...otherwise, we check if we should show the meeting permissions steps
      if (this.perDeviceOnboardingInfo.shouldShowMeetingOnboardingSystemPermissions) {
        return "system-permissions-meeting-intro";
      }

      // ...otherwise, we check if we should show the voice permissions steps
      if (this.perDeviceOnboardingInfo.shouldShowVoiceOnboardingSystemPermissions) {
        return "system-permissions-voice-permissions";
      }
    }

    // Otherwise, start with user-profile (or the configured step)
    return this.welcomeStartStep;
  }

  markStepCompleted(stepId: string) {
    runInAction(() => {
      if (!this.rfiCompletedSteps.includes(stepId)) {
        this.rfiCompletedSteps.push(stepId);
      }
    });
  }

  setWelcomeStartStep(stepId: WelcomeStartStep) {
    runInAction(() => {
      this.welcomeStartStep = stepId;
    });
  }

  public static createFromRemoteData({
    data,
    store,
    perDeviceOnboardingInfo,
  }: {
    data: ApiSchema["PlatformInfoOnboarding"];
    perDeviceOnboardingInfo: PerDeviceOnboardingInfoObservable;
  } & WithAppStore) {
    // Type-safe property access with fallbacks for new RFI properties
    type ExtendedOnboardingData = ApiSchema["PlatformInfoOnboarding"] & {
      should_show_rfi?: boolean;
      should_show_icp_rfi?: boolean;
      should_show_import_cta?: boolean;
      should_show_heads_up_coach_mark?: boolean;
      rfi_start_step?: WelcomeStartStep;
      rfi_completed_steps?: string[];
    };
    const extendedData = data as ExtendedOnboardingData;

    return new PlatformOnboardingInfoObservable({
      shouldShowIcpRfi: extendedData.should_show_icp_rfi ?? extendedData.should_show_rfi ?? false,
      shouldShowImportCta:
        extendedData.should_show_import_cta ?? data.should_show_import_cta ?? false,
      shouldShowHeadsUpCoachMark: extendedData.should_show_heads_up_coach_mark ?? false,
      rfiStartStep: extendedData.rfi_start_step ?? "user-profile",
      rfiCompletedSteps: extendedData.rfi_completed_steps ?? [],
      store,
      perDeviceOnboardingInfo,
    });
  }
}
